import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Search, BookOpen, BriefcaseBusiness, Loader2, UserRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import api, { resolveImg } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/PageHeader";

const Guru = () => {
  const [teachers, setTeachers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  useSeo("Daftar Guru", "Tenaga pendidik profesional Madrasah Ibtidaiyah Futuhiyyah Doro, Pekalongan.");

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/teachers${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setTeachers(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <PageHeader title="Daftar Guru & Staf" subtitle="Tenaga pendidik profesional yang berdedikasi mendidik putra-putri kita." crumbs={[{ label: "Daftar Guru" }]} />
      <div className="mx-auto max-w-6xl px-6 py-14">
        <div className="mx-auto mb-10 max-w-md">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari nama, jabatan, atau mata pelajaran..."
              data-testid="teacher-search-input"
              className="rounded-full pl-11"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>
        ) : teachers.length === 0 ? (
          <div className="py-20 text-center text-slate-400" data-testid="teacher-empty-state">
            <UserRound className="mx-auto h-12 w-12 opacity-40" />
            <p className="mt-3">Tidak ada guru yang cocok dengan pencarian.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {teachers.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                data-testid={`teacher-card-${t.id}`}
                className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md"
              >
                <div className="aspect-square overflow-hidden bg-slate-100">
                  {t.photo_url ? (
                    <img src={resolveImg(t.photo_url)} alt={t.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-slate-300"><UserRound className="h-20 w-20" /></div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-heading text-base font-semibold text-slate-800">{t.name}</h3>
                    <Badge variant="secondary" className="shrink-0 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{t.status}</Badge>
                  </div>
                  <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-600"><BriefcaseBusiness className="h-3.5 w-3.5 text-emerald-600" /> {t.position}</p>
                  {t.subject && <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><BookOpen className="h-3.5 w-3.5 text-emerald-600" /> {t.subject}</p>}
                  {t.bio && <p className="mt-3 text-xs leading-relaxed text-slate-400 line-clamp-2">{t.bio}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Guru;
